import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ZapIcon, CheckCircleIcon, CalendarIcon, MessageSquareIcon } from 'lucide-react';
export function Hero() {
  return <section className="relative min-h-screen flex items-center pt-28 pb-16 lg:pt-20 lg:pb-0 dot-pattern" data-id="element-72">
      {/* Decorative background elements */}
      <div className="absolute top-1/4 left-10 w-64 h-64 bg-primary/10 rounded-full blur-[100px] -z-10" data-id="element-73" />
      <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-secondary/10 rounded-full blur-[120px] -z-10" data-id="element-74" />

      <div className="max-w-7xl mx-auto px-6 w-full" data-id="element-75">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-8 items-center" data-id="element-76">
          {/* Left Column: Content */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col items-start" data-id="element-77">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface border border-primary/20 text-primary text-sm font-medium mb-8 shadow-[0_0_15px_rgba(0,180,216,0.1)]" data-id="element-78">
              <ZapIcon className="w-4 h-4" data-id="element-79" />
              Automatización con IA para Convertir Interesados
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.15] mb-8 tracking-tight text-white" data-id="element-80">
              Tu Negocio Ya Tiene Interesados. Conviértelos en Clientes -{' '}
              <span className="text-[#02B5D8]" data-id="element-81">Automáticamente.</span>
            </h1>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="relative mb-8 w-full max-w-md" data-id="element-82">
              <div className="absolute -inset-2 bg-primary/20 rounded-2xl blur-2xl -z-10" data-id="element-83" />
              <div className="relative rounded-2xl overflow-hidden border border-primary/30 shadow-[0_0_40px_rgba(0,180,216,0.2)]" data-id="element-84">
                <img src="https://cdn.magicpatterns.com/uploads/jJocRmAGPdtXPUvszrTMXU/la_foto.jpg" alt="Fundador de CONECKTA" className="w-full h-auto object-cover" data-id="element-85" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/40 via-transparent to-transparent pointer-events-none" data-id="element-86" />
              </div>
            </motion.div>

            <p className="text-lg md:text-xl text-muted mb-10 max-w-xl leading-relaxed" data-id="element-87">
              Un asistente de IA que responde al instante, califica a cada
              persona y agenda citas por ti — para que crezcas, fidelices y te
              mantengas un paso adelante.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto" data-id="element-88">
              <a href="https://wa.link/cesnp8" target="_blank" rel="noopener noreferrer" className="btn-cta w-full sm:w-auto group" data-id="element-89">
                Quiero Más Citas Agendadas
                <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-90" />
              </a>
            </div>
            <p className="text-sm text-muted mt-5 flex items-center gap-2" data-id="element-91">
              <CheckCircleIcon className="w-4 h-4 text-primary" data-id="element-92" />
              Llamada estratégica gratis · Sin compromiso
            </p>
          </motion.div>

          {/* Right Column: Pipeline Visual */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative w-full max-w-md mx-auto lg:ml-auto" data-id="element-93">
            <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent rounded-3xl -z-10 blur-xl" data-id="element-94" />

            <div className="flex flex-col gap-5 relative" data-id="element-95">
              {/* Connecting Line */}
              <div className="absolute left-[28px] top-[30px] bottom-[30px] w-0.5 bg-gradient-to-b from-primary/80 via-primary/40 to-transparent z-0" data-id="element-96" />

              {/* Nodes */}
              {[{
              icon: MessageSquareIcon,
              title: 'Llega un Interesado',
              desc: 'Por cualquier canal — orgánico, referidos o anuncios',
              color: 'text-white',
              bg: 'bg-surface',
              border: 'border-primary/20'
            }, {
              icon: ZapIcon,
              title: 'Respuesta Instantánea',
              desc: 'En menos de 5 segundos',
              color: 'text-yellow-400',
              bg: 'bg-surface',
              border: 'border-primary/20',
              ml: 'ml-4'
            }, {
              icon: CheckCircleIcon,
              title: 'Interesado Calificado',
              desc: 'Filtro automático',
              color: 'text-green-400',
              bg: 'bg-surface',
              border: 'border-primary/20',
              ml: 'ml-8'
            }, {
              icon: CalendarIcon,
              title: 'Cita Agendada',
              desc: 'Directo a tu calendario',
              color: 'text-primary',
              bg: 'bg-surface',
              border: 'border-primary/50',
              ml: 'ml-12',
              glow: true
            }].map((node, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4 + index * 0.15
            }} className={`relative z-10 flex items-center gap-4 bg-surface/90 backdrop-blur-sm border p-4 rounded-2xl shadow-lg ${node.ml || ''} ${node.glow ? 'shadow-[0_0_20px_rgba(0,180,216,0.15)] border-primary/40' : node.border}`} data-id="element-97">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${node.bg} border ${node.border}`} data-id="element-98">
                    <node.icon className={`w-6 h-6 ${node.color}`} data-id="element-99" />
                  </div>
                  <div data-id="element-100">
                    <h3 className="font-bold text-white text-base md:text-lg" data-id="element-101">
                      {node.title}
                    </h3>
                    <p className="text-xs md:text-sm text-muted" data-id="element-102">{node.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}